import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  if (expiryDate < today) return "expired";
  if (expiryDate <= thirtyDaysLater) return "expiring";
  return "valid";
};

const expiryBadgeClass = (status) => {
  if (status === "expired") return "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300";
  if (status === "expiring") return "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300";
  return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300";
};

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
  return { daysInMonth, startingDay };
};

export function SerialNumberExpiryCalendar({ calendarMonth, setCalendarMonth, calendarData = [] }) {
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarData.filter((s) => s.expiry_date === dateStr);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(calendarMonth);
    const days = [];
    const today = new Date().toISOString().split('T')[0];

    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-muted/20"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
      const dateStr = date.toISOString().split('T')[0];
      const events = getEventsForDate(date);
      const isToday = dateStr === today;
      const isPast = dateStr < today;

      days.push(
        <div
          key={day}
          className={`h-24 border border-border/50 p-1 overflow-hidden ${isToday ? 'bg-primary/10 ring-2 ring-primary' : ''}`}
        >
          <div className={`text-sm font-medium mb-1 ${isPast ? 'text-muted-foreground' : ''}`}>
            {day}
          </div>
          <div className="space-y-0.5 overflow-y-auto max-h-16">
            {events.slice(0, 3).map((event, idx) => {
              const status = getExpiryStatus(event.expiry_date);
              return (
                <div
                  key={event.id || `event-${day}-${idx}`}
                  className={`text-xs px-1 py-0.5 rounded truncate ${expiryBadgeClass(status)}`}
                  title={`${event.product_name} - ${event.customer_name || 'Kein Kunde'}`}
                >
                  {event.product_name}
                </div>
              );
            })}
            {events.length > 3 && (
              <div className="text-xs text-muted-foreground">+{events.length - 3} weitere</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Ablaufkalender</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium min-w-32 text-center">
              {calendarMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCalendarMonth(new Date())}>
              Heute
            </Button>
          </div>
        </div>
        <div className="flex gap-4 mt-2">
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/50" />
            <span>Abgelaufen</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded bg-amber-100 dark:bg-amber-900/50" />
            <span>Läuft bald ab</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/50" />
            <span>Gültig</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
            <div key={day} className="bg-muted p-2 text-center text-sm font-medium">
              {day}
            </div>
          ))}
          {renderCalendar()}
        </div>
      </CardContent>
    </Card>
  );
}
